import { CountryAndCode, countryCodeToCountryName, getAllCountryCallingCodes, parsePhone } from '@alluradirect/react';
import { AppSelect, SelectOptions } from 'components/core/Select/AppSelect';
import { useMemo, useState } from 'react';

export const useCountryCallingCodePicker = (phoneNumber?: string) => {
  const initialInfo: CountryAndCode | undefined = useMemo(() => {
    if (!phoneNumber) return undefined;
    return getInitialCountryInfo(phoneNumber);
  }, [phoneNumber]);

  const [countryCode, setCountryCode] = useState<CountryAndCode['countryCode']>(initialInfo?.countryCode ?? 'CA');
  const [callingCode, setCallingCode] = useState<CountryAndCode['callingCode']>(initialInfo?.callingCode ?? '1');
  const [countryName, setCountryName] = useState<CountryAndCode['countryName']>(initialInfo?.countryName ?? 'Canada');

  return {
    countryCode,
    setCountryCode,
    callingCode,
    setCallingCode,
    countryName,
    setCountryName,
  };
};

export type CallingCodePicker = ReturnType<typeof useCountryCallingCodePicker>;

interface Props {
  disabled?: boolean;
}

export const CountryCallingCodePicker = ({
  countryCode,
  setCallingCode,
  setCountryCode,
  setCountryName,
  disabled = false,
}: CallingCodePicker & Props) => {
  const countryAndCodes = useMemo(() => {
    const allCountriesAndCodes = getAllCountryCallingCodes();
    allCountriesAndCodes.sort((a, b) => {
      if (a.countryName > b.countryName) {
        return 1;
      }
      if (a.countryName === b.countryName) return 0;
      return -1;
    });
    return allCountriesAndCodes;
  }, []);

  const onChange = (e: any) => {
    const res = countryAndCodes.find(country => {
      return country.countryCode == e.target.value;
    });

    if (res) {
      setCallingCode(res.callingCode);
      setCountryCode(res.countryCode);
      setCountryName(res.countryName);
    }
  };

  const options: SelectOptions = useMemo(() => {
    return countryAndCodes.map(item => {
      return {
        value: item.countryCode,
        label: `${item.countryName} (+${item.callingCode})`,
      };
    });
  }, [countryAndCodes]);

  return (
    <AppSelect label={'Phone Number'} options={options} value={countryCode} onChange={onChange} disabled={disabled} />
  );
};

function getInitialCountryInfo(phoneNumber: string): CountryAndCode {
  const res = parsePhone(phoneNumber);
  const countryName = (countryCodeToCountryName as any)[res.country];

  return {
    countryCode: res.country as CountryAndCode['countryCode'],
    callingCode: res.countryCode,
    countryName,
  };
}
